import React, { useState, useEffect } from 'react';
import { updateAdminConfig, updateNotification } from '../../utils/api';
import { applyAccent } from '../Appearance';

const PRESETS = [
  { name: 'Indigo', hex: '#6366f1' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Cyan', hex: '#06b6d4' },
  { name: 'Teal', hex: '#14b8a6' },
  { name: 'Green', hex: '#22c55e' },
  { name: 'Violet', hex: '#8b5cf6' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Rose', hex: '#f43f5e' },
];

const AppearanceAdmin = ({ config, loadConfig, flash }) => {
  const [accent, setAccent] = useState(config.themeAccent || '#6366f1');
  const [savingAccent, setSavingAccent] = useState(false);

  const n0 = config.notification || {};
  const [enabled, setEnabled] = useState(!!n0.enabled);
  const [title, setTitle] = useState(n0.title || '');
  const [body, setBody] = useState(n0.body || '');
  const [footer, setFooter] = useState(n0.footer || '');
  const [buttonText, setButtonText] = useState(n0.buttonText || 'Got it');
  const [frequency, setFrequency] = useState(n0.frequency || 'once');
  const [savingNotif, setSavingNotif] = useState(false);

  useEffect(() => {
    setAccent(config.themeAccent || '#6366f1');
    const nn = config.notification || {};
    setEnabled(!!nn.enabled); setTitle(nn.title || ''); setBody(nn.body || '');
    setFooter(nn.footer || ''); setButtonText(nn.buttonText || 'Got it');
    setFrequency(nn.frequency || 'once');
  }, [config]);

  const pick = (hex) => { setAccent(hex); applyAccent(hex); };  // live preview

  const saveAccent = async () => {
    setSavingAccent(true);
    try {
      await updateAdminConfig({ themeAccent: accent });
      applyAccent(accent);
      flash('Theme color saved — applied across admin + website');
      loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setSavingAccent(false);
  };

  const saveNotif = async () => {
    if (enabled && !title.trim() && !body.trim()) { flash('Add a title or body first', 'error'); return; }
    setSavingNotif(true);
    try {
      const r = await updateNotification({ enabled, title, body, footer, buttonText, frequency });
      flash(r.message || 'Saved');
      loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setSavingNotif(false);
  };

  return (
    <div className="fade-in">
      <style>{`
        .ap-card { padding: 20px 22px; border-radius: 14px; margin-bottom: 18px; }
        .ap-card h3 { margin: 0 0 4px; font-size: 15px; }
        .ap-desc { margin: 0 0 16px; font-size: 12.5px; opacity: .7; line-height: 1.5; }
        .ap-swatches { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
        .ap-swatch {
          width: 40px; height: 40px; border-radius: 11px; border: 2px solid transparent;
          cursor: pointer; transition: transform .12s, box-shadow .12s;
          box-shadow: 0 2px 6px rgba(0,0,0,.15);
        }
        .ap-swatch:hover { transform: translateY(-2px); }
        .ap-swatch.active { border-color: var(--text); box-shadow: 0 0 0 3px var(--accent-glow); }
        .ap-custom { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
        .ap-custom label { font-size: 13px; font-weight: 600; }
        .ap-color { width: 44px; height: 40px; padding: 0; border: 1px solid var(--border); border-radius: 9px; background: none; cursor: pointer; }
        .ap-hex { width: 130px; font-family: var(--font-mono); text-transform: lowercase; }
        .ap-preview { display: flex; align-items: center; gap: 14px; }
        .ap-preview-dot { width: 26px; height: 26px; border-radius: 50%; box-shadow: 0 0 0 4px var(--accent-glow); }
        .ap-mt { margin-top: 16px; }

        .ap-notif-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .ap-enable {
          display: flex; align-items: center; justify-content: space-between; gap: 14px;
          min-width: 190px; padding: 10px 14px; background: var(--bg-sidebar);
          border: 1px solid var(--border); border-radius: 12px; cursor: pointer;
        }
        .ap-enable-info { display: flex; flex-direction: column; gap: 3px; text-align: left; }
        .ap-enable-label { font-weight: 700; font-size: 13px; }
        .ap-enable-state { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
        .ap-enable-state.on { color: #16a34a; }
        .ap-enable-state.off { color: #ef4444; }
        .ap-toggle { position: relative; width: 44px; height: 25px; border-radius: 999px; background: rgba(128,128,128,.35); transition: background .2s; flex-shrink: 0; }
        .ap-toggle.on { background: #16a34a; }
        .ap-toggle-knob { position: absolute; top: 3px; left: 3px; width: 19px; height: 19px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.35); transition: transform .2s; }
        .ap-toggle.on .ap-toggle-knob { transform: translateX(19px); }
        .ap-form { display: grid; gap: 14px; margin-top: 8px; }
        .ap-field label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; }
        .ap-field .input { width: 100%; }
        .ap-textarea { resize: vertical; font-family: inherit; line-height: 1.5; }

        .ap-live { margin-top: 20px; }
        .ap-live-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; opacity: .6; }
        .ap-preview-card {
          margin-top: 10px; max-width: 400px; border: 1px solid var(--border);
          border-radius: 14px; overflow: hidden; background: var(--bg-card);
        }
        .ap-preview-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 16px; border-bottom: 1px solid var(--border);
          background: linear-gradient(135deg, var(--accent-dim), transparent 75%);
          font-size: 15px;
        }
        .ap-preview-x { opacity: .5; }
        .ap-preview-body { padding: 14px 16px; font-size: 13.5px; line-height: 1.6; color: var(--text-2); white-space: pre-wrap; word-break: break-word; }
        .ap-preview-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 16px; border-top: 1px solid var(--border); flex-wrap: wrap; }
        .ap-preview-note { font-size: 12px; opacity: .6; }
        .ap-preview-btn { padding: 8px 16px; border-radius: 9px; color: #fff; font-size: 13px; font-weight: 600; }
      `}</style>

      <h2 className="adm-title">Appearance &amp; Notifications</h2>

      {/* ── Theme color ── */}
      <div className="card ap-card">
        <h3>Theme Accent Color</h3>
        <p className="ap-desc">Pick the brand accent used everywhere — admin panel and the public website, in both light and dark mode. Click a swatch to preview live, then Save.</p>
        <div className="ap-swatches">
          {PRESETS.map(p => (
            <button key={p.hex}
              className={`ap-swatch ${(accent || '').toLowerCase() === p.hex.toLowerCase() ? 'active' : ''}`}
              style={{ background: p.hex }} title={p.name} onClick={() => pick(p.hex)} />
          ))}
        </div>
        <div className="ap-custom">
          <label>Custom</label>
          <input type="color" value={accent} onChange={e => pick(e.target.value)} className="ap-color" />
          <input type="text" value={accent} onChange={e => pick(e.target.value)} className="input ap-hex" placeholder="#6366f1" />
        </div>
        <div className="ap-preview">
          <span className="ap-preview-dot" style={{ background: accent }} />
          <button className="btn btn-primary" onClick={saveAccent} disabled={savingAccent}>{savingAccent ? 'Saving...' : 'Save Color'}</button>
        </div>
      </div>

      {/* ── Notification popup ── */}
      <div className="card ap-card">
        <div className="ap-notif-head">
          <h3>Notification Popup</h3>
          <button type="button" role="switch" aria-checked={enabled} className="ap-enable" onClick={() => setEnabled(v => !v)}>
            <span className="ap-enable-info">
              <span className="ap-enable-label">Show popup</span>
              <span className={`ap-enable-state ${enabled ? 'on' : 'off'}`}>{enabled ? 'Enabled' : 'Disabled'}</span>
            </span>
            <span className={`ap-toggle ${enabled ? 'on' : ''}`}><span className="ap-toggle-knob" /></span>
          </button>
        </div>
        <p className="ap-desc">One popup shows to each visitor <strong>once per browser session</strong>. Saving/publishing again re-shows it to everyone. Long messages scroll inside the popup.</p>
        <div className="ap-form">
          <div className="ap-field"><label>Title (header)</label><input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Welcome to ROBO Token" /></div>
          <div className="ap-field"><label>Body</label><textarea className="input ap-textarea" rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="Your announcement message..." /></div>
          <div className="ap-field"><label>Footer note (optional)</label><input className="input" value={footer} onChange={e => setFooter(e.target.value)} placeholder="e.g. — Team ROBO" /></div>
          <div className="ap-field"><label>Button text</label><input className="input" value={buttonText} onChange={e => setButtonText(e.target.value)} placeholder="Got it" /></div>
          <div className="ap-field"><label>How often to show</label>
            <select className="input" value={frequency} onChange={e => setFrequency(e.target.value)}>
              <option value="once">Once per user — shows a single time (until you republish)</option>
              <option value="every">Every visit — shows each time the user opens the site</option>
            </select>
          </div>
        </div>
        <button className="btn btn-primary ap-mt" onClick={saveNotif} disabled={savingNotif}>
          {savingNotif ? 'Saving...' : (enabled ? 'Publish Notification' : 'Save (kept disabled)')}
        </button>

        <div className="ap-live">
          <span className="ap-live-label">Live preview</span>
          <div className="ap-preview-card">
            <div className="ap-preview-header"><strong>{title || 'Announcement'}</strong><span className="ap-preview-x">×</span></div>
            <div className="ap-preview-body">{body || 'Your message will appear here...'}</div>
            <div className="ap-preview-footer">
              <span className="ap-preview-note">{footer}</span>
              <span className="ap-preview-btn" style={{ background: accent }}>{buttonText || 'Got it'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceAdmin;
